import React, { useState, useEffect, useContext } from "react";
import { GoogleApiContext } from "../../contexts/GoogleApiContext";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import {
  FormControl,
  FormLabel,
  Box,
  FormErrorMessage,
  Input,
  Flex,
  Text,
  Icon,
} from "@chakra-ui/react";
import {
  white,
  newBorder,
  backgroundLight,
  textPrimary,
  textSecondary,
} from "../../utils/colors";

import { FaGoogle } from "react-icons/fa6";

const FormControlAddressFull = ({
  currentValue,
  label,
  onAddressSelect,
  isRequired,
  isTouched,
  setIsTouched,
  isError,
  errorMessage,
  w,
  placeholder,
  labelFontSize,
  onBlur,
  zIndex = 1, // Default zIndex if not provided
}) => {
  const { isApiLoaded } = useContext(GoogleApiContext);
  const [postalCodes, setPostalCodes] = useState({});

  // 🔴 Always call the hook (to avoid conditional hook error)
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: isApiLoaded
      ? {}
      : { componentRestrictions: { country: "LT" } }, // Prevent using API if not loaded
  });

  const [inputValue, setInputValue] = useState(currentValue || "");
  const [isSearching, setIsSearching] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);

  useEffect(() => {
    if (currentValue) {
      setInputValue(currentValue);
    }
  }, [currentValue]);

  useEffect(() => {
    if (isApiLoaded && isSearching) {
      setValue(inputValue);
    }
  }, [isApiLoaded, inputValue, isSearching, setValue]);

  const handleSelect = (address) => {
    setIsTouched && setIsTouched(true);

    getGeocode({ address })
      .then((results) => {
        const addressComponents = results[0].address_components;

        let localCity = "";
        let localZip = "";
        let localStreet = "";
        let localCountry = "";
        let localState = "";
        let localHouseNumber = "";

        addressComponents.forEach((component) => {
          if (component.types.includes("locality")) {
            localCity = component.long_name;
          } else if (component.types.includes("postal_code")) {
            localZip = component.long_name;
          } else if (component.types.includes("route")) {
            localStreet = component.long_name;
          } else if (component.types.includes("country")) {
            localCountry = component.short_name;
          } else if (component.types.includes("administrative_area_level_1")) {
            localState = component.long_name;
          } else if (component.types.includes("street_number")) {
            localHouseNumber = component.long_name;
          }
        });

        const displayAddress = `${localStreet} ${localHouseNumber}, ${localCity}, ${localZip}, ${localCountry}`;

        // console.log("address", address);
        // console.log("addressComponents", addressComponents);

        // Directly use the selected address as the value
        setInputValue(""); // Temporarily clear the input value to force update
        setTimeout(() => {
          setInputValue(displayAddress); // Set the actual address after clearing
          setValue(address, false); // Update places autocomplete library
        }, 0);

        // console.log("inputValue", inputValue);

        clearSuggestions();

        onAddressSelect({
          street: localStreet + " " + localHouseNumber,
          city: localCity,
          zip: localZip,
          state: localState,
          country: localCountry,
          display: address,
        });
      })
      .catch((error) => {
        console.log("Error: ", error);
      });

    setIsSearching(false);
  };

  const handleInput = (e) => {
    const newInputValue = e.target.value;

    setIsTouched && setIsTouched(true);
    setInputValue(newInputValue);

    if (!isSearching) {
      setIsSearching(true);
    }
  };

  // Event handler to set isInputFocused to true when input is focused
  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      if (!isInputFocused) {
        return null;
      }

      const postalCode = postalCodes[place_id] || ""; // Get postal code from state or leave empty initially

      // Fetch postal code asynchronously
      if (!postalCodes[place_id]) {
        fetchPostalCode(suggestion);
      }

      return (
        <Flex
          key={place_id}
          onClick={() => handleSelect(suggestion.description)}
          align="center"
          p="0rem 1rem"
          h="3rem"
          borderBottom={`1px solid ${newBorder}`}
          _last={{ borderBottom: "none" }}
          _hover={{ bg: backgroundLight, cursor: "pointer" }}
        >
          <Text fontSize="sm" color={textPrimary}>
            {main_text}{" "}
            <Text as="span" fontSize="sm" color={textSecondary}>
              {secondary_text} {postalCode && `, ${postalCode}`}
            </Text>
          </Text>
        </Flex>
      );
    });

  // Function to fetch postal code asynchronously when rendering suggestions
  const fetchPostalCode = async (suggestion) => {
    const { place_id } = suggestion;

    if (postalCodes[place_id]) {
      // Return the cached postal code if available
      return postalCodes[place_id];
    }

    try {
      const results = await getGeocode({ placeId: place_id });
      const addressComponents = results[0].address_components;
      const postalCodeComponent = addressComponents.find((component) =>
        component.types.includes("postal_code")
      );

      const postalCode = postalCodeComponent
        ? postalCodeComponent.long_name
        : "";
      setPostalCodes((prev) => ({ ...prev, [place_id]: postalCode }));
      return postalCode;
    } catch (error) {
      console.log("Error fetching postal code: ", error);
      return "";
    }
  };

  // const handleBlur = () => {
  //   if (!inputValue.trim()) {
  //     console.log("Resetting address because inputValue is empty");

  //     setInputValue(""); // Ensure input field is empty

  //     // Prevent empty values causing ", , "
  //     const cleanedAddress = {
  //       street: "",
  //       city: "",
  //       zip: "",
  //       state: "",
  //       country: "",
  //       display: "", // Ensure display is properly cleared
  //     };

  //     onAddressSelect(cleanedAddress); // Reset address state
  //   }
  // };

  // console.log("currentValue", currentValue);
  // console.log(["inputValue", inputValue]);

  // console.log(["inputValue", inputValue]);
  // console.log(["value", value]);

  if (!isApiLoaded) {
    return (
      <Text fontSize="sm" color="red.500">
        Google API is still loading...
      </Text>
    );
  }

  // console.log("✅ Google API Loaded:", isApiLoaded);
  // console.log("✅ usePlacesAutocomplete Ready:", ready);

  // console.log("apiLoaded", isApiLoaded);
  // console.log("tenan");

  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={isTouched && isError}
      w={w}
      position="relative"
      zIndex={zIndex}
    >
      <FormLabel
        fontSize={labelFontSize ? labelFontSize : "sm"}
        position="relative"
        color={textPrimary}
      >
        {label}
        {isError && isTouched && (
          <Box position="absolute" top={-1} right={0} zIndex={2}>
            <FormErrorMessage fontSize="xs">{errorMessage}</FormErrorMessage>
          </Box>
        )}
      </FormLabel>
      <Input
        bg={white}
        w="100%"
        type="text"
        value={inputValue}
        onFocus={handleInputFocus}
        onChange={handleInput}
        onBlur={onBlur}
        disabled={!ready || !isApiLoaded}
        placeholder={placeholder}
        color={textPrimary}
        borderRadius="0.75rem"
        size="lg"
        fontSize={["1rem", "0.938rem"]}
        fontWeight="500"
        border={`1px solid ${newBorder}`}
        boxShadow="none !important"
      />
      <Flex
        position="absolute"
        top="2.25rem"
        right="1rem"
        w="2rem"
        h="2rem"
        bg={white}
        borderRadius="10rem"
        align="center"
        justify="center"
        zIndex={99}
        pointerEvents="none"
      >
        <Icon as={FaGoogle} w="1rem" h="1rem" color={textSecondary} />
      </Flex>

      {status === "OK" && (
        <Flex
          direction="column"
          w="100%"
          position="absolute"
          zIndex={2}
          bg={white}
          borderRadius="0.75rem"
          mt={2}
          boxShadow="1px 1px 6px rgba(0, 0, 0, 0.3)"
          overflow="hidden"
        >
          {renderSuggestions()}
        </Flex>
      )}
    </FormControl>
  );
};

export default FormControlAddressFull;
